import { samplePages, selectDiverse, cloneProductCard, allowedProduct, isMask } from './yoon-diverse-products.js?v=20261009-10';
import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#getRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        if (e.name !== 'AbortError') this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #getRecommendations(productId, sectionId, intent) {
    if (this.dataset.diverse !== 'true' || intent !== 'related') {
      return this.#fetchCachedRecommendations(productId, sectionId, intent);
    }
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    try {
      const documents = await Promise.all([...new Set([1, ...samplePages(this.dataset.catalogCount)])].map(async (page) => {
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('product_id', productId);
        url.searchParams.set('section_id', sectionId);
        url.searchParams.set('intent', intent);
        url.searchParams.set('page', String(page));
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Recommendation request failed');
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      }));
      if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
      const doc = documents[0];
      const list = doc.querySelector('.resource-list[data-has-recommendations]');
      if (!list) throw new Error('Recommendation grid unavailable');
      const selected = selectDiverse(documents.flatMap(d => [...d.querySelectorAll('.resource-list__item[data-product-id]')]), Number(this.dataset.displayLimit) || 8, productId, 'recommendations-' + sectionId);
      if (!selected.length) throw new Error('No available products');
      const scroller = list.classList.contains('resource-list__carousel') ? list.querySelector('slideshow-slides') : null;
      if (scroller) {
        scroller.replaceChildren(...selected.map((node, index) => {
          const slide = node.closest('slideshow-slide').cloneNode(false);
          slide.style.setProperty('--slideshow-timeline', '--slide-' + index);
          slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
          slide.append(cloneProductCard(node, index));
          return slide;
        }));
        list.querySelector('slideshow-component').style.setProperty('--slideshow-timeline', selected.map((_, index) => '--slide-' + index).join(','));
      } else {
        list.replaceChildren(...selected.map(cloneProductCard));
      }
      const component = doc.querySelector('product-recommendations');
      if (component) component.dataset.recommendationsPerformed = 'true';
      return { success: true, data: doc.body.innerHTML };
    } catch (error) {
      if (controller.signal.aborted) throw error;
      return this.#fetchCachedRecommendations(productId, sectionId, intent);
    }
  }

  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      let text = await response.text();
      if (this.dataset.diverse === 'true' && intent === 'related') {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const list = doc.querySelector('.resource-list[data-has-recommendations]');
        const cards = list ? [...list.querySelectorAll('.resource-list__item[data-product-id]')] : [];
        let kept = 0, masks = 0;
        for (const card of cards) {
          if (!allowedProduct(card) || (isMask(card) && masks >= 1) || card.dataset.productId === String(productId) || card.dataset.available !== 'true' || kept >= (Number(this.dataset.displayLimit) || 8)) {
            (card.closest('slideshow-slide') || card).remove();
          } else { kept++; if (isMask(card)) masks++; }
        }
        text = doc.body.innerHTML;
      }
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

